import { useState, useEffect, useCallback } from 'react';
import * as DocumentPicker from 'expo-document-picker';
import { supabase } from '@/lib/supabase';
import { StudentDocument } from '@/lib/types';

export function useStudentDocuments(studentId: string) {
  const [documents, setDocuments] = useState<StudentDocument[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    const { data } = await supabase
      .from('student_documents')
      .select('*')
      .eq('student_id', studentId)
      .order('uploaded_at', { ascending: false });
    setDocuments(data ?? []);
    setLoading(false);
  }, [studentId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  async function uploadDocument() {
    const result = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'image/*'],
      copyToCacheDirectory: true,
    });
    if (result.canceled) return;

    const asset = result.assets[0];
    const mimeType = asset.mimeType ?? 'application/octet-stream';
    const fileType: 'pdf' | 'image' = mimeType === 'application/pdf' ? 'pdf' : 'image';
    const path = `${studentId}/${Date.now()}-${asset.name}`;

    const response = await fetch(asset.uri);
    const blob = await response.blob();

    const { error: uploadError } = await supabase.storage
      .from('student-documents')
      .upload(path, blob, { contentType: mimeType });
    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from('student-documents')
      .getPublicUrl(path);

    await supabase.from('student_documents').insert({
      student_id: studentId,
      name: asset.name,
      file_url: urlData.publicUrl,
      file_type: fileType,
    });

    await fetchDocuments();
  }

  return { documents, loading, uploadDocument, refetch: fetchDocuments };
}
